import {
    boot,
    isEraroError,
    EraroError,
    eraroCodesMap,
} from '@dazn/bootstrap-lib';

import { SafeModeConfig } from '../configuration';
import { SAFEMODE_STAUS } from '../constants/safemode.constants';
import { SetCurrentChapter } from '../public-api/lib/SetCurrentChapter';

import {
    displayEndOfTheWorldError,
    EndOfTheWorldInput,
} from './endOfTheWorld/displayEndOfTheWorldError';
import { loadErrorChapter } from './loadErrorChapter';

const isStartupError = (error: EraroError) => {
    const { category, code, internal } = error;
    return (
        category === eraroCodesMap.category.StartupService &&
        code === eraroCodesMap.code.AppLoadError &&
        (
            [
                eraroCodesMap.internal.Ignore,
                eraroCodesMap.internal.NetworkError,
                eraroCodesMap.internal.ServerError,
            ] as string[]
        ).includes(internal)
    );
};

const isChapterLoadError = (error: EraroError) => {
    const { category, code, internal } = error;
    return (
        category === eraroCodesMap.category.ChapterLoading &&
        code === eraroCodesMap.code.UnableToLoadChapter &&
        internal === eraroCodesMap.internal.Ignore
    );
};

const isRestrictedCountryError = (error: EraroError) =>
    error.code === eraroCodesMap.code.RestrictedCountry ||
    (error.category === eraroCodesMap.category.UserProfileService &&
        error.internal === eraroCodesMap.internal.AccessDenied);

const isUserProfileNotAuthenticated = (error: EraroError) => {
    const { BadRequest, Unauthenticated, NotFound } = eraroCodesMap.internal;
    return (
        error.category === eraroCodesMap.category.UserProfileService &&
        ([BadRequest, Unauthenticated, NotFound] as string[]).includes(
            error.internal,
        )
    );
};

const isNotAuthenticated = (error: EraroError) =>
    error.code === eraroCodesMap.code.UnathorizedRefreshTokenError ||
    isUserProfileNotAuthenticated(error);

const isUserProfileTooManyRequests = (error: EraroError) =>
    error.category === eraroCodesMap.category.UserProfileService &&
    error.internal === eraroCodesMap.internal.TooManyRequests;

const isUserProfileServerError = (error: EraroError) =>
    error.category === eraroCodesMap.category.UserProfileService &&
    error.internal === eraroCodesMap.internal.ServerError;

export interface HandleError {
    loadChapter: SetCurrentChapter;
    restrictedCountryChapterName: boot.config.ChapterName;
    errorChapterName: boot.config.ChapterName;
    currentChapterName: string;
    error: Error;
    sendErrorEvent: EndOfTheWorldInput['sendErrorEvent'];
    authChapterName: boot.config.ChapterName;
    defaultCategory: string;
    defaultCode: string;
    defaultInternal: string;
    fetchSafeMode: () => Promise<any>;
    getSafeModeConfig: () => SafeModeConfig;
    getBrandName: () => boot.brand.BrandName;
}

export const handleError = ({
    loadChapter,
    restrictedCountryChapterName,
    errorChapterName,
    currentChapterName,
    error,
    sendErrorEvent,
    authChapterName,
    defaultCategory,
    defaultCode,
    defaultInternal,
    fetchSafeMode,
    getSafeModeConfig,
    getBrandName,
}: HandleError) => {
    const brandName = getBrandName();
    // eslint-disable-next-line @applitools/compat/no-async-await
    const displayBasedOnSafeMode = async () => {
        const safeModeHomepageURL: string = getSafeModeConfig().homePageURL;
        try {
            const response = await fetchSafeMode();

            const safeModeHomepageURL = getSafeModeConfig().homePageURL;

            displayEndOfTheWorldError({
                error,
                sendErrorEvent,
                safeModeHomepageURL,
                safeModeOn: response.status === SAFEMODE_STAUS.RED,
                brandName,
            });
        } catch (err) {
            displayEndOfTheWorldError({
                error,
                sendErrorEvent,
                safeModeHomepageURL,
                safeModeOn: false,
                brandName,
            });
        }
    };

    // making the error page non-indexable
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    meta.classList.add('protected');
    document.head.appendChild(meta);

    if (isEraroError(error)) {
        if (isNotAuthenticated(error)) {
            return loadChapter({
                chapterName: authChapterName,
                chapterData: {},
                chapterPath: '',
            });
        }

        window.newrelic?.noticeError(
            `${error.category}-${error.code}-${error.internal}: ${error.message}`,
        );

        if (isRestrictedCountryError(error)) {
            return loadChapter({
                chapterName: restrictedCountryChapterName,
                chapterData: {
                    // restrictedCountry: true,
                    loadGlobalLandingPage: true,
                },
            });
        }

        const userProfileCategory = eraroCodesMap.category.UserProfileService;
        let code;
        let internal;

        if (eraroCodesMap.category.UserProfileService === error.category) {
            if (isUserProfileTooManyRequests(error)) {
                code = eraroCodesMap.code.ServiceTooManyAttemps;
                internal = eraroCodesMap.internal.TooManyRequests;
            } else if (isUserProfileServerError(error)) {
                code = eraroCodesMap.code.GenericError;
                internal = eraroCodesMap.internal.ServerError;
            }

            if (code && internal) {
                return loadErrorChapter(
                    loadChapter,
                    userProfileCategory,
                    code,
                    internal,
                    errorChapterName,
                    currentChapterName,
                    error,
                );
            }
        }
        if (isStartupError(error)) {
            return displayBasedOnSafeMode();
        }
        if (isChapterLoadError(error)) {
            return Promise.resolve();
        }
    }

    if (currentChapterName !== errorChapterName) {
        try {
            return loadErrorChapter(
                loadChapter,
                defaultCategory,
                defaultCode,
                defaultInternal,
                errorChapterName,
                currentChapterName,
                error,
            );
        } catch (e) {}
    }
    return displayBasedOnSafeMode();
};
